/**
 * WebSocket Context
 * Manages WebSocket connection for real-time messaging
 * Includes browser notifications and sound alerts
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from '../lib/axios'; // Use configured axios with CSRF
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) throw new Error('useWebSocket must be used within WebSocketProvider');
  return context;
};

// Subtle pop sound using Web Audio API
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.1);
    
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.15);
  } catch (e) {
    // Silent fail - audio not supported
  }
};

export const WebSocketProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [lastNotification, setLastNotification] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const activeConversationIdRef = useRef(null); // Ref to avoid stale closure in WebSocket callback
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Keep ref in sync with state
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);
  
  // Check and load notification preferences
  useEffect(() => {
    const savedPref = localStorage.getItem('notificationsEnabled');
    const savedSound = localStorage.getItem('soundEnabled');
    if (savedPref !== null) setNotificationsEnabled(savedPref === 'true');
    if (savedSound !== null) setSoundEnabled(savedSound === 'true');
    
    // Check if notifications are already granted (only if Notification API exists)
    if ('Notification' in window && Notification.permission === 'granted') {
      setNotificationsEnabled(true);
    }
  }, []);
  
  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      return false;
    }
    
    if (Notification.permission === 'granted') {
      setNotificationsEnabled(true);
      localStorage.setItem('notificationsEnabled', 'true');
      return true;
    }
    
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      setNotificationsEnabled(granted);
      localStorage.setItem('notificationsEnabled', String(granted));
      return granted;
    }
    
    return false;
  }, []);
  
  // Toggle sound
  const toggleSound = useCallback((enabled) => {
    setSoundEnabled(enabled);
    localStorage.setItem('soundEnabled', String(enabled));
  }, []);
  
  // Show browser notification
  const showBrowserNotification = useCallback((msg) => {
    // Don't show if notifications disabled or tab is focused and viewing same conversation
    if (!notificationsEnabled) return;
    
    // Use ref to get current active conversation (avoids stale closure)
    const currentActiveConversation = activeConversationIdRef.current;
    if (document.hasFocus() && currentActiveConversation === msg.conversation_id) return;
    
    try {
      // Build notification tag with listing_id for per-product grouping
      const notificationTag = msg.listing_id 
        ? `chat-${msg.listing_id}-${msg.sender_id}` 
        : msg.conversation_id;
      
      // Include listing context in notification body if available
      const bodyText = msg.listing_title 
        ? `Re: ${msg.listing_title}\n${msg.content?.substring(0, 60) || 'New message'}${msg.content?.length > 60 ? '...' : ''}`
        : msg.content?.substring(0, 80) + (msg.content?.length > 80 ? '...' : '') || 'You have a new message';
      
      const notification = new Notification(msg.sender_name || 'New Message', {
        body: bodyText,
        icon: msg.sender_avatar || '/logo192.png',
        tag: notificationTag, // Groups notifications per listing per sender
        requireInteraction: false,
        silent: true // We handle sound ourselves
      });
      
      notification.onclick = () => {
        window.focus();
        notification.close();
        // The conversation will be opened via the lastMessage state update
      };
      
      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);
      
      // Play sound if enabled
      if (soundEnabled) {
        playNotificationSound();
      }
    } catch (e) {
      // Notifications not supported or blocked
    }
  }, [notificationsEnabled, soundEnabled, activeConversationId]);

  const connect = useCallback(() => {
    if (!user || wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const wsUrl = `${WS_URL}/api/ws`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (token) {
          ws.send(JSON.stringify({ type: 'auth', token: token }));
        } else {
          axios.get(`${API}/auth/ws-token`).then(res => {
            if (res.data.ws_token) {
              ws.send(JSON.stringify({ type: 'auth', token: res.data.ws_token }));
            }
          }).catch(() => {
            ws.close();
          });
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Filter out webpack dev server messages
          if (['hot', 'liveReload', 'reconnect', 'overlay', 'hash', 'warnings', 'ok', 'content-changed'].includes(data.type)) {
            return;
          }
          
          // Handle connection confirmation
          if (data.type === 'connection' && data.status === 'connected') {
            setIsConnected(true);
            reconnectAttempts.current = 0;
            return;
          }
          
          setLastMessage(data);

          // Handle different message types
          if (data.type === 'new_message') {
            const msg = data.message;
            
            // Only increment unread count and show notification if:
            // 1. Message is from someone else (not our own message)
            // 2. We're not currently viewing that conversation
            const isOwnMessage = msg?.sender_id === user?.id;
            const currentActiveConversation = activeConversationIdRef.current;
            const msgConversationId = msg?.conversation_id;
            const isViewingConversation = document.hasFocus() && currentActiveConversation && currentActiveConversation === msgConversationId;
            
            // Debug log - only when needed to trace issues
            if (process.env.NODE_ENV === 'development') {
              console.log('[WS] Message received:', {
                isOwnMessage,
                currentActiveConversation,
                msgConversationId,
                match: currentActiveConversation === msgConversationId,
                hasFocus: document.hasFocus(),
                isViewingConversation,
                willIncrementBadge: !isOwnMessage && !isViewingConversation
              });
            }
            
            if (!isOwnMessage && !isViewingConversation) {
              setUnreadCount(prev => prev + 1);
              // Show browser notification for messages from others
              showBrowserNotification(msg);
            }
          } else if (data.type === 'new_notification') {
            // Real-time notification update
            const notification = data.notification;
            if (notification) {
              setLastNotification(notification);
              
              // Play sound for new notifications if enabled
              if (soundEnabled && !document.hasFocus()) {
                playNotificationSound();
              }
            }
          } else if (data.type === 'typing') {
            setTypingUsers(prev => ({
              ...prev,
              [data.sender_id]: data.is_typing
            }));
            if (data.is_typing) {
              setTimeout(() => {
                setTypingUsers(prev => ({
                  ...prev,
                  [data.sender_id]: false
                }));
              }, 3000);
            }
          }
        } catch (err) {
          // Silent fail for malformed messages
        }
      };

      ws.onerror = () => {
        // Reconnection logic will handle retries
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;

        // Attempt to reconnect if not a deliberate close
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts && user) {
          reconnectAttempts.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      // Silent fail - connection errors are handled by retry logic
    }
  }, [user, token, showBrowserNotification]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logout');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const sendTypingIndicator = useCallback((recipientId, isTyping = true) => {
    sendMessage({
      type: 'typing',
      recipient_id: recipientId,
      is_typing: isTyping
    });
  }, [sendMessage]);

  const sendReadReceipt = useCallback((conversationId, senderId) => {
    sendMessage({
      type: 'read_receipt',
      conversation_id: conversationId,
      sender_id: senderId
    });
  }, [sendMessage]);

  // Connect when user is available (with small delay to ensure cookies are set)
  useEffect(() => {
    if (user) {
      // Small delay to ensure auth cookies are properly set after login
      const connectTimeout = setTimeout(() => {
        connect();
      }, 500);
      return () => clearTimeout(connectTimeout);
    }
    return () => {
      disconnect();
    };
  }, [user, connect, disconnect]);

  // Fetch initial unread count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API}/messages/unread/count`);
        setUnreadCount(response.data.count);
      } catch (error) {
        // Silent fail
      }
    };
    fetchUnreadCount();
  }, [user]);

  return (
    <WebSocketContext.Provider value={{
      isConnected,
      lastMessage,
      lastNotification,
      typingUsers,
      unreadCount,
      setUnreadCount,
      sendMessage,
      sendTypingIndicator,
      sendReadReceipt,
      connect,
      disconnect,
      // Notification controls
      notificationsEnabled,
      soundEnabled,
      requestNotificationPermission,
      toggleSound,
      setActiveConversationId
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
